import type { AppConfigResponse, BotCheckAction, EmailCodeSent, EmailVerifyResponse, Player } from "@station-keeping/contracts";
import { BALANCE, SIM_VERSION, type OfflineReport } from "@station-keeping/sim";
import { create } from "zustand";
import { ApiClient } from "../api/client";
import { ApiError, NetworkError, isTransient } from "../api/errors";
import { API_URL, APP_VERSION } from "../env";
import { t } from "../i18n";
import { botCheckToken } from "../platform/bot-check";
import { takeLoginLink, type LoginLink } from "../platform/login-link";
import { readItem, removeItem, writeItem } from "../platform/storage";
import { uuid } from "../platform/uuid";
import { ServerClock } from "../sync/clock";
import { SyncManager, type SyncHooks, type SyncStatus } from "../sync/sync";
import { GameEngine } from "./engine";
import { startSandbox } from "./sandbox";
import { setEngine } from "./runtime";
import { useGameUi } from "./store";

export type BlockReason = "offline" | "upgrade" | "suspended" | "unavailable" | "error";

export type Phase =
  | { kind: "booting" }
  /** Aucun compte connecté sur cet appareil : un compte est obligatoire, on se connecte par e-mail et code. */
  | { kind: "signin" }
  | { kind: "playing" }
  | { kind: "sandbox" }
  /** La station est ouverte ailleurs : un autre appareil, ou un autre onglet de ce navigateur. */
  | { kind: "takenOver"; by: "device" | "tab" }
  | { kind: "blocked"; reason: BlockReason };

interface SessionState {
  phase: Phase;
  player: Player | null;
  config: AppConfigResponse | null;
  network: SyncStatus;
  /** Session en cours de réouverture (retour d'absence, état relu) : la station est figée un instant. */
  reopening: boolean;
  /** Récapitulatif « Pendant ton absence », jusqu'à ce que le joueur le ferme. */
  offlineReport: OfflineReport | null;
  /** La session d'un compte a expiré sur cet appareil : l'écran de connexion le dit. */
  sessionExpired: boolean;
  /** Lien de connexion reçu par e-mail, que l'écran de compte valide. */
  loginLink: LoginLink | null;
  /** Le serveur a une version plus récente de la simulation : il faut mettre le jeu à jour. */
  updateAvailable: boolean;
}

export const useSession = create<SessionState>(() => ({
  phase: { kind: "booting" },
  player: null,
  config: null,
  network: "online",
  reopening: false,
  offlineReport: null,
  sessionExpired: false,
  loginLink: null,
  updateAvailable: false,
}));

const set = useSession.setState;

/** E-mail du dernier compte connecté sur cet appareil (pour reproposer la connexion). */
const ACCOUNT_KEY = "sk.account.v1";

/** Identifiant de cet onglet, pour qu'un seul onglet du navigateur joue à la fois. */
const TAB_ID = uuid();
const CHANNEL = "station-keeping";

const OFFLINE_RETRY_MS = [3000, 10_000, 30_000] as const;

/**
 * La partie en ligne : compte (obligatoire, connexion par e-mail et code), ouverture de session sur la
 * station du serveur, sauvegarde continue, reprises et changements de compte. Une seule instance, `session`.
 */
class GameSession {
  readonly api = new ApiClient(API_URL, APP_VERSION);
  readonly clock = new ServerClock();
  private engine: GameEngine | null = null;
  private sync: SyncManager | null = null;
  private channel: BroadcastChannel | null = null;
  private running: Promise<void> | null = null;
  private lifecycleBound = false;
  private offlineRetries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private lastRecovery = 0;

  /** Démarrage : réglages, compte, station. */
  boot(): Promise<void> {
    return this.exclusive(async () => {
      set({ phase: { kind: "booting" }, reopening: false });
      this.bindLifecycle();
      await this.bootSteps();
    });
  }

  /** Station locale à temps accéléré, sans serveur (développement). */
  sandbox(): void {
    set({ phase: { kind: "sandbox" } });
    startSandbox();
  }

  /** Rouvre la session : retour d'une longue absence, état à relire, reprise sur cet appareil. */
  reopen(): Promise<void> {
    if (this.running) return this.running;
    this.sync?.stop();
    this.engine?.setPaused(true);
    set({ reopening: true });
    return this.exclusive(async () => {
      if (!useSession.getState().player) {
        await this.bootSteps();
        return;
      }
      await this.openSession();
    });
  }

  /** « Reprendre ici » : cet appareil (ou cet onglet) reprend la main sur la station. */
  takeOver(): Promise<void> {
    return this.reopen();
  }

  /** Nouvel essai après une coupure ou une erreur. */
  retry(): void {
    clearTimeout(this.retryTimer);
    if (useSession.getState().player?.email && this.engine) void this.reopen();
    else void this.boot();
  }

  dismissOfflineReport(): void {
    set({ offlineReport: null });
  }

  // --- Compte ------------------------------------------------------------------------------------

  async requestCode(email: string): Promise<EmailCodeSent> {
    const siteKey = useSession.getState().config?.botCheck.siteKey ?? null;
    const token = siteKey ? await this.botCheck(siteKey, "email_code") : undefined;
    return this.api.requestCode(email, token);
  }

  async verifyCode(email: string, code: string): Promise<EmailVerifyResponse> {
    // Le serveur doit connaître la partie de cet appareil avant de choisir quelle station garder.
    if (useSession.getState().phase.kind === "playing") await this.sync?.flush();
    const result = await this.api.verifyCode(email, code);
    if (result.outcome !== "choice_required") await this.applyIdentity(result.player);
    return result;
  }

  async chooseStation(choiceToken: string, keep: "current" | "account"): Promise<void> {
    const result = await this.api.chooseStation({ choiceToken, keep, confirm: true });
    if (result.outcome !== "choice_required") await this.applyIdentity(result.player);
  }

  /** Déconnexion : la station reste sur le compte ; cet appareil revient à l'écran de connexion. */
  async logout(): Promise<void> {
    await this.sync?.flush().catch(() => undefined);
    this.leaveStation();
    await this.api.logout().catch(() => undefined);
    removeItem(ACCOUNT_KEY);
    set({ player: null, offlineReport: null, sessionExpired: false, phase: { kind: "signin" } });
  }

  /** Suppression immédiate et définitive du compte et de sa station ; retour à l'écran de connexion. */
  async deleteAccount(): Promise<void> {
    this.sync?.stop();
    try {
      await this.api.deleteMe();
    } catch (error) {
      void this.reopen();
      throw error;
    }
    this.leaveStation();
    removeItem(ACCOUNT_KEY);
    set({ player: null, offlineReport: null, sessionExpired: false, phase: { kind: "signin" } });
  }

  /** Cet appareil lâche la station : plus de sauvegarde, plus de temps qui passe. */
  private leaveStation(): void {
    this.sync?.stop();
    this.engine?.setPaused(true);
    useGameUi.getState().reset();
  }

  /**
   * Le compte a changé (connexion, compte créé, choix de station) : depuis l'écran de connexion, ou si ce
   * n'est plus le même joueur, on (r)ouvre la station.
   */
  private async applyIdentity(player: Player): Promise<void> {
    const { player: previous, phase } = useSession.getState();
    if (previous?.id !== player.id) useGameUi.getState().reset();
    set({ player, sessionExpired: false });
    if (player.email) writeItem(ACCOUNT_KEY, player.email);
    if (previous?.id !== player.id || phase.kind !== "playing") await this.reopen();
  }

  // --- Démarrage et session ------------------------------------------------------------------------

  /** Une seule séquence (démarrage ou réouverture) à la fois ; ses erreurs passent par `fail`. */
  private exclusive(work: () => Promise<void>): Promise<void> {
    if (this.running) return this.running;
    this.running = (async () => {
      try {
        await work();
        this.offlineRetries = 0;
      } catch (error) {
        this.fail(error);
      } finally {
        set({ reopening: false });
      }
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async bootSteps(): Promise<void> {
    await this.loadConfig();
    const player = await this.currentPlayer();
    // Un compte est obligatoire : sans session (ou avec un ancien profil invité, à sécuriser), on se connecte.
    if (!player?.email) {
      set({ player, phase: { kind: "signin" } });
      return;
    }
    set({ player });
    writeItem(ACCOUNT_KEY, player.email);
    await this.openSession();
  }

  private async loadConfig(): Promise<AppConfigResponse> {
    const { data, sentAt, receivedAt } = await this.api.config();
    this.clock.sample(data.serverTime, sentAt, receivedAt);
    set({ config: data, updateAvailable: data.simVersion > SIM_VERSION });
    return data;
  }

  /** Le joueur connecté sur cet appareil, `null` sans session (premier lancement, session expirée). */
  private async currentPlayer(): Promise<Player | null> {
    try {
      return (await this.api.me()).player;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
    }
    // Un compte s'était connecté ici : sa session a expiré, l'écran de connexion le dit.
    if (readItem(ACCOUNT_KEY)) set({ sessionExpired: true });
    return null;
  }

  private async openSession(): Promise<void> {
    this.sync?.stop();
    this.claimTab();
    const { data: session, sentAt, receivedAt } = await this.api.openSession(SIM_VERSION);
    this.clock.sample(session.serverTime, sentAt, receivedAt);
    const anchor = { tick: session.anchor.tick, at: Date.parse(session.anchor.serverTime) };

    let engine = this.engine;
    let sync = this.sync;
    if (engine && sync) {
      engine.reset(session.state, anchor, session.lastSeq);
    } else {
      engine = new GameEngine(session.state, anchor, this.clock, session.lastSeq);
      // Au-delà du seuil d'absence, le serveur compte le temps passé (et en fait le récapitulatif).
      engine.setLagGuard(BALANCE.offline.thresholdSeconds, () => void this.reopen());
      sync = new SyncManager(engine, this.clock, this.syncHooks());
      this.engine = engine;
      this.sync = sync;
      setEngine(engine);
    }
    sync.start({ leaseId: session.leaseId, revision: session.revision, tick: session.tick });
    sync.setHeartbeat(document.visibilityState !== "hidden");
    set({
      phase: { kind: "playing" },
      network: "online",
      ...(session.offlineReport ? { offlineReport: session.offlineReport } : {}),
    });
  }

  private syncHooks(): SyncHooks {
    return {
      send: (request, keepalive) => this.api.sync(request, keepalive),
      onResync: (_resync, reason) => {
        if (reason !== "server_truth") useGameUi.getState().toast(t("sync.resynced"), "info");
      },
      onTakenOver: () => {
        this.engine?.setPaused(true);
        if (useSession.getState().phase.kind !== "takenOver") set({ phase: { kind: "takenOver", by: "device" } });
      },
      onReopen: () => void this.reopen(),
      onFatal: (error) => this.fail(error),
      onStatus: (network) => set({ network }),
      newBatchId: uuid,
    };
  }

  private async botCheck(siteKey: string, action: BotCheckAction): Promise<string> {
    try {
      return await botCheckToken(siteKey, action);
    } catch {
      throw new ApiError(503, "BOT_CHECK_UNAVAILABLE", "Vérification anti-robot indisponible");
    }
  }

  /** Une erreur qui arrête la partie : écran bloquant, nouvel essai automatique si c'est une coupure. */
  private fail(error: unknown): void {
    this.sync?.stop();
    this.engine?.setPaused(true);
    const block = (reason: BlockReason) => set({ phase: { kind: "blocked", reason } });
    if (error instanceof ApiError) {
      if (error.status === 426 || error.code === "UPGRADE_REQUIRED") return block("upgrade");
      if (error.code === "ACCOUNT_SUSPENDED") return block("suspended");
      if (error.code === "STATION_UNAVAILABLE") return block("unavailable");
      // Session perdue en route (compte supprimé ailleurs, jetons révoqués) : retour à la connexion.
      if ((error.status === 401 || error.status === 404) && Date.now() - this.lastRecovery > 30_000) {
        this.lastRecovery = Date.now();
        set({ player: null });
        setTimeout(() => void this.boot(), 0);
        return;
      }
    }
    if (error instanceof NetworkError || isTransient(error)) {
      block("offline");
      const delay = OFFLINE_RETRY_MS[Math.min(this.offlineRetries, OFFLINE_RETRY_MS.length - 1)];
      this.offlineRetries += 1;
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.retry(), delay);
      return;
    }
    console.error(error);
    block("error");
  }

  // --- Onglets et cycle de vie de la page ---------------------------------------------------------

  /** Annonce aux autres onglets que celui-ci prend la station ; ils s'effacent. */
  private claimTab(): void {
    if (typeof BroadcastChannel === "undefined") return;
    if (!this.channel) {
      this.channel = new BroadcastChannel(CHANNEL);
      this.channel.onmessage = (event: MessageEvent<{ type?: string; tab?: string } | null>) => {
        if (event.data?.type !== "claim" || event.data.tab === TAB_ID) return;
        if (useSession.getState().phase.kind !== "playing") return;
        set({ phase: { kind: "takenOver", by: "tab" } });
        this.engine?.setPaused(true);
        // Les dernières actions partent avant de céder la place (au mieux : l'autre onglet arrive).
        const sync = this.sync;
        void sync?.flush({ keepalive: true }).finally(() => sync.stop());
      };
    }
    this.channel.postMessage({ type: "claim", tab: TAB_ID });
  }

  private bindLifecycle(): void {
    if (this.lifecycleBound) return;
    this.lifecycleBound = true;
    document.addEventListener("visibilitychange", () => {
      const hidden = document.visibilityState === "hidden";
      this.sync?.setHeartbeat(!hidden);
      if (useSession.getState().phase.kind !== "playing") return;
      // Au retour, `flush` rattrape le temps passé (ou rouvre la session au-delà du seuil d'absence).
      void this.sync?.flush(hidden ? { keepalive: true } : {});
    });
    window.addEventListener("pagehide", () => void this.sync?.flush({ keepalive: true }));
    window.addEventListener("online", () => {
      const phase = useSession.getState().phase;
      if (phase.kind === "blocked" && phase.reason === "offline") this.retry();
      else void this.sync?.flush();
    });
    window.addEventListener("hashchange", () => this.readLoginLink());
    this.readLoginLink();
  }

  private readLoginLink(): void {
    const link = takeLoginLink();
    if (link) set({ loginLink: link });
  }

  /** L'écran de connexion a pris le lien en charge. */
  consumeLoginLink(): void {
    set({ loginLink: null });
  }

  /** Adresse proposée dans l'écran de connexion. */
  lastAccountEmail(): string {
    return readItem(ACCOUNT_KEY) ?? "";
  }
}

export const session = new GameSession();
